/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import { CheckBoxState } from "@itwin/core-react";
import { DelayLoadedTreeNodeItem, ITreeDataProvider, TreeNodeItem } from "@itwin/components-react";
import { AbstractTreeNodeLoaderWithProvider, TreeDataProvider } from "@itwin/components-react";
import { TreeDataChangesListener, TreeEventHandler } from "@itwin/components-react";
import { TreeCheckboxStateChangeEventArgs, TreeSelectionModificationEventArgs, TreeSelectionReplacementEventArgs } from "@itwin/components-react";
import { PropertyRecord } from "@itwin/appui-abstract";
import { BeEvent } from "@itwin/core-bentley";
import "./Trees.scss";

export interface NavigationNode {
  id: string;
  label: string;
  parent?: string;
  child?: NavigationNode[];
}

export class NavigationDataProvider implements ITreeDataProvider {
  public onTreeNodeChanged = new BeEvent<TreeDataChangesListener>();
  public _treeRoot?: NavigationNode;
  private _search: string = "";
  private _selection: string[] = [];

  constructor(root: NavigationNode, keyword?: string) {
    // this.setNavigationRoot(root);
    // if (keyword) this.setSearchString(keyword);
  }

  public setNavigationRoot = (root: NavigationNode) => {
    this._treeRoot = root;
  }

  private getNavigationRoot = (): NavigationNode => {
    return this._treeRoot!;
  };

  public setSearchString = (search: string) => {
    this._search = search.toUpperCase();
    console.log("TreeHandler search: " + this._search);
  }

  public setSelection = (elements: string[]) => {
    this._selection = elements;
  }

//  public async getNodesCount(parent?: TreeNodeItem) {
  public getNodesCount =  async (parent?: TreeNodeItem) => {
      const root = this.getNavigationRoot()!;
    var node: NavigationNode;
    var len = 0;

    if (parent === undefined) {
      node = root;
    } else {
      node = this.findTreeNode(root, parent.id);
    }

    if (node && node.child) {
      for (const child of node.child) {
        if (this.findString(child, this._search)) len++;
      }
      //      len = node.child.length;
    }

    console.log("getNodesCount.count: " + node.id + "=" + len + ", Search: " + this._search);
    return len;
  }

//  public async getNodes(parent?: TreeNodeItem) {
  public getNodes = async (parent?: TreeNodeItem) => {
      const root = this.getNavigationRoot()!;
    var node: NavigationNode;
    var nodes = new Array();

    if (parent === undefined) {
      node = root;
    } else {
      node = this.findTreeNode(root, parent.id);
    }

    console.log("getNodes.parent: " + node.id);
    if (node && node.hasOwnProperty("child")) {
      for (const child of node.child!) {
        const hasChild = child.hasOwnProperty("child");
        if (this.findString(child, this._search)) {
          const expand = this.isSelected(child.id);
          nodes.push(this.createNode(child.id, child.label, hasChild, expand));
        }
      }
    }
    return nodes;
  }

  private findTreeNode = (node: NavigationNode, id: string): NavigationNode => {
    var found: NavigationNode = undefined!;
    if (node.id === id) {
        return node;
    }

    if (node.hasOwnProperty("child")) {
      for (const child of node.child!) {
        found = this.findTreeNode(child, id);
        if (found) return found;
      }
    }

    return found;
  }

  private findString = (node: NavigationNode, search: string): boolean => {
    var found: boolean = false;

    if (!search || search === "" || search.length === 0) return true;
    if (node.label.toUpperCase().includes(search)) return true;

    if (node.hasOwnProperty("child")) {
      for (const child of node.child!) {
        found = this.findString(child, search);
        if (found) return found;
      }
    }

    return found;
  }

  private isSelected = (id : string): boolean => {
    for (const tag of this._selection) {
      if (tag === id) return true;
    }

    return false;
  }

  private createNode = (id: string, label: string, hasChildren: boolean, expand: boolean): DelayLoadedTreeNodeItem => {
    return {
      id: id,
      label: PropertyRecord.fromString(label),
      hasChildren: hasChildren,
      autoExpand: expand
    }
  }
     
}

//===========================================================================================
export class NavigationSelectionHandler extends TreeEventHandler {
  private _selectedNode?: TreeNodeItem;
  private _selectionCallback?: (id: string) => void;
  constructor(nodeLoader: AbstractTreeNodeLoaderWithProvider<TreeDataProvider>) {
    super({
      modelSource: nodeLoader.modelSource,
      nodeLoader,
      collapsedChildrenDisposalEnabled: true,
    });
  }

  public setSelectionCallback = (callback: (id: string) => void) => {
    this._selectionCallback = callback;
  }

  public getCurrentSelection = (): string => {
    const id = this._selectedNode?.id!;
    return id;
  };

  /** Selects or deselects nodes until event is handled, handler is disposed selection replaced event occurs.  */
  public onSelectionModified = (event: TreeSelectionModificationEventArgs) => {
    // call base selection handling
    const baseSubscription = super.onSelectionModified(event);
    console.log("onSelectionModified");

    // subscribe to selection modifications to get selected and deselected items and do some additional
    // work with them
    const subscription = event.modifications.subscribe({
      next: ({ selectedNodeItems, deselectedNodeItems }) => {
        // here goes code that should be invoked when selection in tree is modified. It could
        // be some additional modifications to tree model or some code that depends on tree selection

        // in case some selected/deselected nodes are not loaded yet this callback will be invoked several times
        // with new 'selectedNodeItems' and 'deselectedNodeItems' when they are loaded

        // modify nodes in tree model by checking/unchecking selected/deselected nodes' checkboxes
        console.log("onSelectionModified:subscribe");
        this.modelSource.modifyModel((model) => {
          selectedNodeItems.forEach((item) => {
            const node = model.getNode(item.id)!;
            node.checkbox.state = CheckBoxState.On;
            console.log("onSelectionModified: " + node.id);
            this._selectedNode = item;
          });
          deselectedNodeItems.forEach((item) => {
            const node = model.getNode(item.id)!;
            node.checkbox.state = CheckBoxState.Off;
          });
        });
      },
    });

    // stop checkboxes handling when base selection handling is stopped
    baseSubscription?.add(subscription);
    return baseSubscription;
  }

  /** Replaces currently selected nodes until event is handled, handler is disposed or another selection replaced event occurs. */
  public onSelectionReplaced = (event: TreeSelectionReplacementEventArgs) => {
    // call base selection handling
    const baseSubscription = super.onSelectionReplaced(event);
    console.log("onSelectionReplaced");

    let firstEmission = true;
    // subscribe to selection replacements to get selected node items and do some additional work
    // with them
    const subscription = event.replacements.subscribe({
      next: ({ selectedNodeItems }) => {
        // here goes code that should be invoked when selection in tree is replaced. It could be some additional
        // modifications to tree model or some code that depends on tree selection

        // in case some selected nodes are not loaded yet, this callback will be invoked several times
        // with new 'selectedNodeItems' when they are loaded

        // modify nodes in tree model by checking selected nodes' checkboxes
        this.modelSource.modifyModel((model) => {
          console.log("onSelectionReplaced:subscribe");

          // on first emission turn off all checkboxes
          if (firstEmission) {
            for (const node of model.iterateTreeModelNodes())
              node.checkbox.state = CheckBoxState.Off;
            firstEmission = false;
          }
          selectedNodeItems.forEach((item) => {
            const node = model.getNode(item.id)!;
            node.checkbox.state = CheckBoxState.On;
            console.log("onSelectionReplaced: " + node.id);
            this._selectedNode = item;
            if (this._selectionCallback) this._selectionCallback(node.id);
          });
        });
      },
    });
    // stop checkboxes handling when base selection handling is stopped
    baseSubscription?.add(subscription);
    return baseSubscription;
  }

  /** Changes nodes checkboxes states until event is handled or handler is disposed */
  public onCheckboxStateChanged = ({
    stateChanges,
  }: TreeCheckboxStateChangeEventArgs) => {
    // call base checkbox handling
    const baseHandling = super.onCheckboxStateChanged({ stateChanges });
    console.log("onCheckboxStateChanged");
    // subscribe to checkbox state changes to new checkbox states and do some additional work with them
    const selectionHandling = stateChanges.subscribe({
      next: (changes) => {
        // there goes code that should invoked when node's checkbox is checked/unchecked. It could be some
        // additional modifications to tree model or some code that depends on tree checkboxes' states

        // modify nodes to be selected/deselected if checkbox was checked or unchecked
        this.modelSource.modifyModel((model) => {
          changes.forEach((change) => {
            const node = model.getNode(change.nodeItem.id)!;
            node.isSelected = change.newState === CheckBoxState.On;
          });
        });
      },
    });
    // stop handling selection when checkboxes handling is stopped
    baseHandling?.add(selectionHandling);
    return baseHandling;
  }
}
